import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, Settings, Building2, Globe, ArrowLeft, Crown, Receipt } from 'lucide-react'
import { Logo } from '../ui/Logo'
import { useI18n } from '../../i18n'
import { DashboardSection } from './DashboardSection'
import { ConfigSection } from './ConfigSection'
import { EnterpriseConfigSection } from './EnterpriseConfigSection'
import { PlanSection } from './PlanSection'
import { OrderSection } from './OrderSection'

type AdminTabKey = 'dashboard' | 'configs' | 'plans' | 'orders' | 'enterprise-config'

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTabKey>('dashboard')
  const { lang, toggleLang, t } = useI18n()

  const tabs: { key: AdminTabKey; label: string; labelEn: string; icon: React.ElementType }[] = [
    { key: 'dashboard', label: '控制台', labelEn: 'Dashboard', icon: BarChart3 },
    { key: 'configs', label: '系统配置', labelEn: 'Configs', icon: Settings },
    { key: 'plans', label: '套餐管理', labelEn: 'Plans', icon: Crown },
    { key: 'orders', label: '订单管理', labelEn: 'Orders', icon: Receipt },
    { key: 'enterprise-config', label: '企业配置', labelEn: 'Enterprise', icon: Building2 },
  ]

  return (
    <div className="h-screen flex flex-col bg-[#fafafa]">
      {/* Header */}
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 sm:px-6 shrink-0 z-30">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-2 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        <Link
          to="/workspace"
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('返回工作台', 'Back')}
        </Link>
        <span className="text-xs text-gray-400 bg-black/[0.04] px-2 py-0.5 rounded-md font-medium">
          {t('管理后台', 'Admin')}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={toggleLang}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === 'zh' ? 'EN' : '中'}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="hidden md:flex w-52 flex-col border-r border-black/[0.04] bg-white/50 py-4 px-3 shrink-0">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2.5 px-3 py-2.5 text-[13px] rounded-xl transition-all mb-1 ${
                  isActive
                    ? 'bg-orange-500/10 text-orange-600 font-medium'
                    : 'text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04]'
                }`}
              >
                <Icon className="h-4 w-4" />
                {lang === 'zh' ? tab.label : tab.labelEn}
              </button>
            )
          })}
        </aside>

        {/* Mobile tabs */}
        <div className="md:hidden flex overflow-x-auto border-b border-black/[0.04] bg-white/50 px-2 py-1 shrink-0 gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center justify-center gap-1 px-3 py-2 text-[12px] rounded-lg transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-orange-500/10 text-orange-600 font-medium'
                    : 'text-gray-400'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {lang === 'zh' ? tab.label : tab.labelEn}
              </button>
            )
          })}
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
            {activeTab === 'dashboard' && <DashboardSection />}
            {activeTab === 'configs' && <ConfigSection />}
            {activeTab === 'plans' && <PlanSection />}
            {activeTab === 'orders' && <OrderSection />}
            {activeTab === 'enterprise-config' && <EnterpriseConfigSection />}
          </div>
        </main>
      </div>
    </div>
  )
}
